// Kopfzeile einer Seite in der App-Ansicht.
//
// Zwei Titel, mit Absicht: der kleine mittig in der Leiste bleibt beim
// Scrollen stehen und sagt, wo man ist; der große darunter läuft mit dem
// Inhalt weg und trägt die Zusammenfassung. Das ist das Muster, das jede
// iOS-App benutzt, und der Grund, warum man dort nie die Orientierung
// verliert, obwohl die Leiste schmal ist.
import type { ReactNode } from 'react';
import Icon from '../gemeinsam/komponenten/Icon';
import { useSprache } from '../gemeinsam/lib/i18n';

export default function Seitenkopf({ titel, zurueck, aktionen, grossTitel, unter }: {
  titel: string;
  /** Fehlt sie, zeigt die Leiste keinen Zurück-Pfeil — oberste Ebene. */
  zurueck?: () => void;
  aktionen?: ReactNode;
  grossTitel?: string;
  unter?: ReactNode;
}) {
  const { t } = useSprache();

  return (
    <>
      <header
        style={{
          // Der Inhalt läuft bis unter die Statusleiste. Der Abstand kommt als
          // Innenabstand, damit der helle Streifen der Leiste bis an den
          // Bildschirmrand reicht und dort nicht abreißt.
          paddingTop: 'calc(0.25rem + var(--sicher-oben))',
          minHeight: 'calc(var(--kopf-h) + var(--sicher-oben))',
        }}
        className="sticky top-0 z-20 flex items-center gap-0.5 border-b border-slate-200/70 bg-white/85 px-1 backdrop-blur-xl dark:border-slate-800/70 dark:bg-slate-900/85"
      >
        {zurueck ? (
          <button
            onClick={zurueck}
            aria-label={t('aktion.zurueck')}
            // 44 px ist die kleinste Fläche, die ein Daumen sicher trifft.
            className="z-10 flex size-11 items-center justify-center rounded-xl text-marke-600 active:opacity-45 dark:text-marke-400"
          >
            {/* Auf Arabisch zeigt der Pfeil nach rechts — dorthin, wo
                „vorher" liegt, wenn die Schrift andersherum läuft. */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
                 className="rtl:rotate-180" aria-hidden>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        ) : (
          <span className="size-11" aria-hidden />
        )}

        {/* Absolut gesetzt, damit der Titel wirklich mittig steht und nicht
            von der Breite der Knöpfe rechts verschoben wird. */}
        <h1 className="pointer-events-none absolute inset-x-0 truncate px-14 text-center text-base font-semibold text-slate-900 dark:text-slate-100">
          {titel}
        </h1>

        <div className="z-10 ms-auto flex items-center">{aktionen}</div>
      </header>

      {grossTitel && (
        <div className="px-5 pb-1 pt-3.5">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {grossTitel}
          </h2>
          {unter && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{unter}</p>}
        </div>
      )}
    </>
  );
}

/** Knopf für den rechten Teil der Leiste — gleiche Trefferfläche wie der Zurück-Pfeil. */
export function KopfKnopf({ symbol, beschriftung, onClick }: {
  symbol: Parameters<typeof Icon>[0]['name'];
  beschriftung: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={beschriftung}
      className="flex size-11 items-center justify-center rounded-xl text-marke-600 active:opacity-45 dark:text-marke-400"
    >
      <Icon name={symbol} groesse={21} />
    </button>
  );
}
